import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Palette, Sun, Moon, Sparkles } from 'lucide-react';
import { seasonalThemes, themeColors, ThemeColor } from '../types/template';
import { saveThemeSettings, getThemeSettings } from '../lib/themes';

interface Props {
  onClose: () => void;
  onThemeChange: (theme: ThemeColor) => void;
  onSeasonalThemeChange: (theme: keyof typeof seasonalThemes | null) => void;
  currentTheme: ThemeColor;
  currentSeasonalTheme: keyof typeof seasonalThemes | null;
}

export function ThemeSettings({
  onClose,
  onThemeChange,
  onSeasonalThemeChange,
  currentTheme,
  currentSeasonalTheme
}: Props) {
  const [activeTab, setActiveTab] = useState<'colors' | 'seasonal'>('colors');

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Personnalisation du thème</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* Onglets */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('colors')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'colors'
                  ? 'bg-purple-100 text-purple-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              <Palette className="w-4 h-4" />
              Couleurs
            </button>
            <button
              onClick={() => setActiveTab('seasonal')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'seasonal'
                  ? 'bg-purple-100 text-purple-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Thèmes saisonniers
            </button>
          </div>

          {activeTab === 'colors' && (
            <div className="space-y-6">
              <div className="grid grid-cols-5 gap-3">
                {(Object.keys(themeColors) as ThemeColor[]).map((color) => (
                  <motion.button
                    key={color}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onThemeChange(color)}
                    className={`w-full aspect-square rounded-lg ${themeColors[color].bg} 
                      ${currentTheme === color ? 'ring-4 ring-offset-2 ring-purple-500' : ''}`}
                  />
                ))}
              </div>

              <div className="flex justify-center gap-4">
                <button
                  onClick={() => onSeasonalThemeChange(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                >
                  <Sun className="w-4 h-4" />
                  Mode clair
                </button>
                <button
                  onClick={() => {
                    // Implémenter le mode sombre
                  }}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center gap-2"
                >
                  <Moon className="w-4 h-4" />
                  Mode sombre
                </button>
              </div>
            </div>
          )}

          {activeTab === 'seasonal' && (
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(seasonalThemes).map(([key, theme]) => (
                <motion.button
                  key={key}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onSeasonalThemeChange(key as keyof typeof seasonalThemes)}
                  className={`p-4 rounded-lg text-white transition-all ${theme.colors.background} ${
                    currentSeasonalTheme === key ? 'ring-4 ring-purple-500' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{theme.icons.profile}</span>
                    <h3 className="font-semibold capitalize">{key}</h3>
                  </div>
                  <p className="text-sm opacity-90">
                    Disponible du {theme.startDate} au {theme.endDate}
                  </p>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}